import React, { useState, useEffect, useCallback } from "react";
import Hero from "./components/Hero";
import CustomersSection from "./components/CustomersSection";
import ShowcaseSection from "./components/ShowcaseSection";
import SponsorsValueStrip from "./components/SponsorsValueStrip";
import FeaturesSection from "./components/FeaturesSection";
import FeatureDetailsModal from "./components/FeatureDetailsModal";
import IntegrationSection from "./components/IntegrationSection";
import HowItWorks from "./components/HowItWorks";
import PricingSection from "./components/PricingSection";
import CTASection from "./components/CTASection";
import DemoModal from "./components/DemoModal";
import Footer from "./components/Footer";

export default function LandingPage() {
  const initialFormData = {
    name: "",
    email: "",
    clubName: "",
    playCricketUrl: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [status, setStatus] = useState("");
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [signupFormKey, setSignupFormKey] = useState(0);

  // Ensure body overflow is enabled when component mounts
  useEffect(() => {
    document.body.style.overflow = "";
    return () => {
      // Cleanup: ensure overflow is restored on unmount
      document.body.style.overflow = "";
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Sending...");

    const apiBase = process.env.REACT_APP_API_BASE_URL || "https://api.snaptrix.co.uk";

    try {
      const response = await fetch(`${apiBase}/app/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          clubName: formData.clubName,
          playCricketUrl: formData.playCricketUrl,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setStatus(
          "Your account will be created along with templates. You will receive an email shortly."
        );
        setFormData(initialFormData);
        setSignupFormKey((k) => k + 1);
        setTimeout(() => {
          setIsDemoModalOpen(false);
          setStatus("");
        }, 4000);
      } else if (response.status === 400 && data.error?.includes("already exists")) {
        setStatus("An account with this email already exists. Please log in.");
      } else {
        setStatus(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setStatus("Something went wrong. Please try again.");
    }
  };

  const openDemoModal = useCallback(() => {
    setIsDemoModalOpen(true);
  }, []);

  const closeDemoModal = useCallback(() => {
    setIsDemoModalOpen(false);
    setStatus("");
  }, []);

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
  };

  const closeFeatureModal = () => {
    setSelectedFeature(null);
  };

  return (
    <div className="min-h-screen bg-white text-snaptrix-gray-700 flex flex-col">
      <main className="flex-1">
        <Hero onBookDemo={openDemoModal} />

        <ShowcaseSection />

        <SponsorsValueStrip />

        <FeaturesSection onFeatureClick={handleFeatureClick} />

        <IntegrationSection />

        <HowItWorks />

        <CustomersSection />

        <PricingSection onBookDemo={openDemoModal} />

        <CTASection onBookDemo={openDemoModal} />
      </main>

      <Footer />

      {/* Feature Details Modal */}
      <FeatureDetailsModal
        feature={selectedFeature}
        onClose={closeFeatureModal}
      />

      {/* Sign Up Modal */}
      <DemoModal
        key={signupFormKey}
        isOpen={isDemoModalOpen}
        onClose={closeDemoModal}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        status={status}
      />
    </div>
  );
}
